import React from 'react';
import { AgentDomain, personaForDomain } from '../utils/agentCatalog';

/**
 * Circular avatar of the persona bound to a conversation `domain`.
 *
 * - Personas with a sprite `avatar` (appraisal pair) crop one gendered cell.
 * - Personas with a single-portrait `avatar` fill the circle (cover).
 * - Personas with `avatar === null` render an initial-letter placeholder.
 */
export const AgentAvatar: React.FC<{
  domain?: AgentDomain | string;
  /** Pixel diameter (default 28). */
  size?: number;
  className?: string;
}> = ({ domain, size = 28, className = '' }) => {
  const persona = personaForDomain(domain);

  if (persona.avatar) {
    if (persona.sprite) {
      // Opaque 2×2 gendered grid — crop one cell, neutral backdrop.
      return (
        <span
          role="img"
          aria-label={persona.name}
          className={`flex-shrink-0 rounded-full bg-gray-100 ring-1 ring-black/5 ${className}`}
          style={{
            width: size,
            height: size,
            backgroundImage: `url(${persona.avatar})`,
            backgroundRepeat: 'no-repeat',
            backgroundSize: '200% 200%',
            backgroundPosition: persona.gender === 'male' ? 'right top' : 'left top',
          }}
        />
      );
    }
    // Transparent portrait — the figure stays on a transparent background; only
    // the frame follows the selected palette (a tinted background clashed with
    // the figure's own colors). `ring-primary-400` recolors with the palette.
    return (
      <span
        role="img"
        aria-label={persona.name}
        className={`flex-shrink-0 rounded-full bg-transparent ring-2 ring-primary-400 ${className}`}
        style={{
          width: size,
          height: size,
          backgroundImage: `url(${persona.avatar})`,
          backgroundRepeat: 'no-repeat',
          backgroundSize: 'cover',
          backgroundPosition: 'center top',
        }}
      />
    );
  }

  // Placeholder: initial letter on a primary-tinted circle.
  return (
    <span
      role="img"
      aria-label={persona.name}
      className={`flex-shrink-0 inline-flex items-center justify-center rounded-full bg-primary-100 text-primary-700 font-semibold ring-1 ring-black/5 ${className}`}
      style={{ width: size, height: size, fontSize: Math.round(size * 0.45) }}
    >
      {persona.name.charAt(0)}
    </span>
  );
};

export default AgentAvatar;
